import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";
import { Progress } from "@/shared/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/ui/dialog";
import { Label } from "@/shared/ui/label";
import { toast } from "sonner";
import { queryKeys } from "@/core/lib/queryKeys";
import {
  Award,
  Plus,
  Globe,
  Building2,
  FileText,
  Users,
  Palette,
} from "lucide-react";

interface Competency {
  id: string;
  name: string;
  description: string | null;
  color: string;
  is_global: boolean;
  certifiedCount?: number;
  coverage?: number;
}

interface WorkInstruction {
  id: string;
  name: string;
  checklist_kind: "startup" | "shift_change";
  instruction_competencies: {
    competency: { id: string; name: string; color: string | null } | null;
  }[];
}

interface CompetencyLibraryProps {
  competencies: Competency[];
  competencyCoverage: Competency[];
  workInstructions: WorkInstruction[];
  searchQuery: string;
  tenantId: string | null;
  factoryId: string;
}

const PRESET_COLORS = [
  "#ef4444", "#f97316", "#f59e0b", "#eab308",
  "#84cc16", "#22c55e", "#10b981", "#14b8a6",
  "#06b6d4", "#0ea5e9", "#3b82f6", "#6366f1",
  "#8b5cf6", "#a855f7", "#d946ef", "#ec4899",
];

export function CompetencyLibrary({
  competencies,
  competencyCoverage,
  workInstructions,
  searchQuery,
  tenantId,
  factoryId,
}: CompetencyLibraryProps) {
  const queryClient = useQueryClient();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newCompetency, setNewCompetency] = useState({
    name: "",
    description: "",
    color: PRESET_COLORS[0],
  });

  const filteredCompetencies = competencyCoverage.filter(
    (c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const globalCompetencies = filteredCompetencies.filter((c) => c.is_global);
  const factoryCompetencies = filteredCompetencies.filter((c) => !c.is_global);

  // Get work instructions that require each competency
  const getInstructionsForCompetency = (compId: string) => {
    return workInstructions.filter((wi) =>
      wi.instruction_competencies.some((wic) => wic.competency?.id === compId)
    );
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!tenantId) {
        throw new Error("No tenant selected");
      }

      const { error } = await supabase.schema('core').from("competencies").insert({
        name: newCompetency.name,
        description: newCompetency.description || null,
        color: newCompetency.color,
        factory_id: factoryId,
        tenant_id: tenantId,
        is_global: false,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.competencies(factoryId) });
      toast.success("Competency created successfully");
      setCreateDialogOpen(false);
      setNewCompetency({ name: "", description: "", color: PRESET_COLORS[0] });
    },
    onError: (error) => {
      toast.error("Failed to create competency");
      console.error(error);
    },
  });

  const CompetencyCard = ({ comp }: { comp: Competency }) => {
    const instructions = getInstructionsForCompetency(comp.id);

    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${comp.color}20` }}
            >
              <Award className="w-5 h-5" style={{ color: comp.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-medium text-sm truncate">{comp.name}</h3>
                <Badge variant="outline" className="text-[10px]">
                  {comp.is_global ? (
                    <>
                      <Globe className="w-2.5 h-2.5 mr-1" />
                      Global
                    </>
                  ) : (
                    <>
                      <Building2 className="w-2.5 h-2.5 mr-1" />
                      Factory
                    </>
                  )}
                </Badge>
              </div>
              {comp.description && (
                <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                  {comp.description}
                </p>
              )}
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Users className="w-3 h-3" />
                  <span>{comp.certifiedCount || 0} certified</span>
                </div>
                {instructions.length > 0 && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <FileText className="w-3 h-3" />
                    <span>{instructions.length} instruction(s)</span>
                  </div>
                )}
              </div>
              {comp.coverage !== undefined && (
                <div className="mt-2">
                  <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                    <span>Coverage</span>
                    <span>{Math.round(comp.coverage)}%</span>
                  </div>
                  <Progress value={comp.coverage} className="h-1.5" />
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Factory Competencies */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">Factory Competencies</h3>
            <Badge variant="secondary">{factoryCompetencies.length}</Badge>
          </div>
          <Button variant="outline" size="sm" onClick={() => setCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Competency
          </Button>
        </div>
        {factoryCompetencies.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-muted-foreground text-sm">
                No factory-specific competencies yet
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setCreateDialogOpen(true)}
              >
                Create First Competency
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {factoryCompetencies.map((comp) => (
              <CompetencyCard key={comp.id} comp={comp} />
            ))}
          </div>
        )}
      </div>

      {/* Global Competencies */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-5 h-5 text-muted-foreground" />
          <h3 className="font-semibold">Global Competencies</h3>
          <Badge variant="secondary">{globalCompetencies.length}</Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {globalCompetencies.map((comp) => (
            <CompetencyCard key={comp.id} comp={comp} />
          ))}
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Factory Competency</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={newCompetency.name}
                onChange={(e) =>
                  setNewCompetency((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g., CNC Machine Operation"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={newCompetency.description}
                onChange={(e) =>
                  setNewCompetency((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder="Brief description of this competency..."
                rows={3}
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Color
              </Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-8 h-8 rounded-lg transition-all ${
                      newCompetency.color === color
                        ? "ring-2 ring-offset-2 ring-primary scale-110"
                        : "hover:scale-105"
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewCompetency((prev) => ({ ...prev, color }))}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!newCompetency.name || createMutation.isPending}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
